import React from 'react';
import { Box, Card, CardContent, Typography, Avatar, Tooltip, useTheme } from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
// import PropTypes from 'prop-types'; // Optional: for prop type validation

const UserMessage = ({ content, timestamp }) => {
  const theme = useTheme();

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        console.warn('UserMessage: Invalid timestamp provided', dateString);
        return "Invalid time";
      }
      return new Intl.DateTimeFormat('en-US', {
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true // Can adjust hour12 as preferred
      }).format(date);
    } catch (error) {
      console.error('UserMessage: Error formatting date', dateString, error);
      return "Error time";
    }
  };

  const formattedTime = formatDate(timestamp);

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'flex-end', // Align to the right
      alignItems: 'flex-start', // Align items to the top of the flex container
      alignSelf: 'flex-end',    // Align the whole Box to the end of its parent flex container
      maxWidth: '80%',
      mt: 1,
      mb: 1,
      pl: '20%', // Ensure it doesn't take full width on very small screens, pushes from left
      boxSizing: 'border-box',
    }}>
      {/* Message Content and Card */}
      <Box sx={{
        order: 1, // Content first visually on the left for LTR, but appears left of avatar due to parent flex-end
        maxWidth: '100%', // MaxWidth relative to its container which is 80% of parent
        mr: 1, // Margin between card and avatar block
      }}>
        <Card sx={{
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          borderRadius: '16px 4px 16px 16px', // Standard "user" bubble shape
          boxShadow: theme.shadows[1], // Subtle shadow
        }}>
          <CardContent sx={{
            py: 1.25, // Adjusted padding
            px: 1.75,
            '&:last-child': { pb: 1.25 }, // Ensure padding is consistent
          }}>
            <Typography
              variant="body1"
              component="div" // Use div to allow block-level children if markdown was ever introduced
              sx={{
                whiteSpace: 'pre-wrap', // Respects newlines and spaces
                wordBreak: 'break-word', // Prevents overflow
              }}
            >
              {content || ''} {/* Ensure content is at least an empty string */}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Avatar and Timestamp */}
      <Box sx={{
        order: 2, // Avatar block second
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        flexShrink: 0, // Prevent avatar block from shrinking
      }}>
        <Avatar sx={{
          bgcolor: theme.palette.primary.main,
          width: 36,
          height: 36,
          mb: 0.5
        }}>
          <PersonIcon fontSize="small" />
        </Avatar>
        {formattedTime && (
          <Tooltip title={timestamp ? new Date(timestamp).toLocaleString() : 'Message time'}>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.65rem', // Slightly smaller for better fit
                lineHeight: 1.1,    // Adjust line height for small font
                color: theme.palette.mode === 'dark' ? theme.palette.grey[400] : theme.palette.grey[700],
                mt: 0.25, // Tiny margin top
              }}
            >
              {formattedTime}
            </Typography>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

/* // Optional PropTypes for development
UserMessage.propTypes = {
  content: PropTypes.string.isRequired,
  timestamp: PropTypes.string, // Assuming timestamp is an ISO string or similar
};
*/

export default UserMessage;